import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { logger } from '@/utils/logger';
import { Helmet } from 'react-helmet';
import { validateEmail } from '@/utils/inputSecurity';
import { sanitizeRedirectUrl } from '@/utils/redirectSecurity';
import { shouldUseRedirect, isInAppBrowser } from '@/utils/browserEnv';
import { authBreadcrumb, authError } from '@/utils/authObservability';
import { mapAuthError } from '@/utils/authErrorMap';
import { publicAsset } from '@/utils/publicAsset';
import { AgeConfirm, AGE_REQUIRED_MESSAGE } from '@/shared/components/AgeConfirm';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn,
    signInWithGoogle,
    signInWithApple,
    startSocialRedirect,
    currentUser
  } = useEnhancedAuth();
  const pageLoadTime = useRef(Date.now());
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  const blockedByAge = () => {
    if (ageConfirmed) return false;
    setStatus('error');
    setError(AGE_REQUIRED_MESSAGE);
    return true;
  };

  // Track page view
  useEffect(() => {
    logAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
      page_name: 'sign_in',
      is_authenticated: false,
      timestamp: new Date().toISOString(),
      referrer: document.referrer,
      mode: 'signin',
      time_on_page: Date.now() - pageLoadTime.current
    });
  }, []);

  // Auto-redirect authenticated users
  useEffect(() => {
    if (currentUser) {
      const urlParams = new URLSearchParams(location.search);
      const from = urlParams.get('from');
      // Use account setup page to handle loading timing issues
      const redirectParams = new URLSearchParams();
      // Security: Sanitize redirect URL to prevent open redirects
      const safeRedirect = sanitizeRedirectUrl(from);
      redirectParams.set('redirect', safeRedirect);
      navigate(`/account-setup?${redirectParams.toString()}`, { replace: true });
    }
  }, [currentUser, navigate, location.search, status]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setError(null);

    const authStartTime = Date.now();
    const normalizedEmail = email.trim();

    const emailValidation = validateEmail(normalizedEmail);
    if (!emailValidation.isValid) {
      const validationMessage = emailValidation.error || 'Please enter a valid email address.';
      setStatus('error');
      setError(validationMessage);

      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_ERROR, {
        method: 'signin',
        error_type: 'invalid_email_format',
        error_message: validationMessage,
        time_taken: 0,
        email_provided: false,
        timestamp: Date.now()
      });
      return;
    }

    try {
      // Track auth start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_STARTED, {
        method: 'signin',
        email_provided: !!normalizedEmail,
        has_referrer: !!location.search,
        timestamp: Date.now()
      });

      await signIn(normalizedEmail, password);

      const urlParams = new URLSearchParams(location.search);
      const from = urlParams.get('from');

      // Use account setup page to handle loading timing issues
      const redirectParams = new URLSearchParams();
      // Security: Sanitize redirect URL to prevent open redirects
      const finalRedirectPath = sanitizeRedirectUrl(from);
      redirectParams.set('redirect', finalRedirectPath);

      // Track successful auth
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_COMPLETED, {
        method: 'signin',
        time_taken: Date.now() - authStartTime,
        has_redirect: !!from,
        redirect_path: finalRedirectPath,
        timestamp: Date.now()
      });

      // Track session start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SESSION_STARTED, {
        method: 'signin',
        timestamp: Date.now()
      });

      navigate(`/account-setup?${redirectParams.toString()}`, { replace: true });
      
    } catch (error) {
      logger.error('SignIn', 'Authentication error:', { error });
      setStatus('error');

      const mapped = mapAuthError(error);
      // Our-fault failures (App Check, server) become code-grouped Rollbar
      // items; user-side outcomes stay breadcrumbs so Rollbar carries signal.
      if (mapped.ourFault) {
        authError(`signin-infra:${mapped.code}`, error, { method: 'signin', error_type: mapped.type });
      } else {
        authBreadcrumb(`signin-user-error:${mapped.code}`, { method: 'signin' });
      }

      setError(mapped.message);

      // Track auth error
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_ERROR, {
        method: 'signin',
        error_type: mapped.type,
        error_message: mapped.message,
        time_taken: Date.now() - authStartTime,
        email_provided: !!normalizedEmail,
        timestamp: Date.now()
      });
    }
  };

  const handleGoogleSignIn = async () => {
    if (blockedByAge()) return;
    setStatus('loading');
    setError(null);

    const authStartTime = Date.now();

    try {
      // Track Google auth start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_STARTED, {
        method: 'google_signin',
        email_provided: false,
        has_referrer: !!location.search,
        timestamp: Date.now()
      });

      // Mobile browsers (in-app webviews AND regular mobile Safari/Chrome)
      // handle popup OAuth unreliably — the returning-user equivalent of the
      // trial-signup killer, seen as "Sign-in cancelled" on real devices.
      // Redirect there; useSocialRedirectCompletion() finishes the sign-in on
      // return. No pending-signup context is stashed: this is a sign-IN, not a
      // signup, so the global completion handler (not TrialSignupFlow) resolves it.
      if (shouldUseRedirect()) {
        await startSocialRedirect('google');
        return; // page navigates away; nothing below runs
      }

      const consent = {
        marketingEmailConsent: false,
        transactionalEmailConsent: true, // Always opt-in for essential service emails (quiz completion, etc.)
        termsAcceptedAt: new Date().toISOString()
      };

      await signInWithGoogle(consent);
      
      const urlParams = new URLSearchParams(location.search);
      const from = urlParams.get('from');
      
      // Use account setup page to handle loading timing issues
      const redirectParams = new URLSearchParams();
      // Security: Sanitize redirect URL to prevent open redirects
      const finalRedirectPath = sanitizeRedirectUrl(from);
      redirectParams.set('redirect', finalRedirectPath);
      
      // Track successful auth
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_COMPLETED, {
        method: 'google_signin',
        time_taken: Date.now() - authStartTime,
        has_redirect: !!from,
        redirect_path: finalRedirectPath,
        timestamp: Date.now()
      });

      // Track session start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SESSION_STARTED, {
        method: 'google_signin',
        timestamp: Date.now()
      });
      
      navigate(`/account-setup?${redirectParams.toString()}`, { replace: true });
      
    } catch (error) {
      logger.error('SignIn', 'Google authentication error:', { error });
      setStatus('error');

      const mapped = mapAuthError(error);
      if (mapped.ourFault) {
        authError(`signin-infra:${mapped.code}`, error, { method: 'google_signin', error_type: mapped.type, in_app_browser: isInAppBrowser() });
      } else {
        authBreadcrumb(`signin-user-error:${mapped.code}`, { method: 'google_signin', in_app_browser: isInAppBrowser() });
      }

      const errorMessage = mapped.message;
      const errorType = mapped.type;

      setError(errorMessage);

      // Track auth error
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_ERROR, {
        method: 'google_signin',
        error_type: errorType,
        error_message: errorMessage,
        time_taken: Date.now() - authStartTime,
        email_provided: false,
        timestamp: Date.now()
      });
    }
  };

  const handleAppleSignIn = async () => {
    if (blockedByAge()) return;
    setStatus('loading');
    setError(null);

    const authStartTime = Date.now();

    try {
      // Track Apple auth start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_STARTED, {
        method: 'apple_signin',
        email_provided: false,
        has_referrer: !!location.search,
        timestamp: Date.now()
      });

      // Mobile browsers handle popup OAuth unreliably (see handleGoogleSignIn).
      // Redirect there; the global completion handler resolves it on return.
      if (shouldUseRedirect()) {
        await startSocialRedirect('apple');
        return; // page navigates away; nothing below runs
      }

      const consent = {
        marketingEmailConsent: false,
        transactionalEmailConsent: true,
        termsAcceptedAt: new Date().toISOString()
      };

      await signInWithApple(consent);

      const urlParams = new URLSearchParams(location.search);
      const from = urlParams.get('from');

      const redirectParams = new URLSearchParams();
      const finalRedirectPath = sanitizeRedirectUrl(from);
      redirectParams.set('redirect', finalRedirectPath);

      // Track successful auth
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_COMPLETED, {
        method: 'apple_signin',
        time_taken: Date.now() - authStartTime,
        has_redirect: !!from,
        redirect_path: finalRedirectPath,
        timestamp: Date.now()
      });

      // Track session start
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SESSION_STARTED, {
        method: 'apple_signin',
        timestamp: Date.now()
      });

      navigate(`/account-setup?${redirectParams.toString()}`, { replace: true });

    } catch (error) {
      logger.error('SignIn', 'Apple authentication error:', { error });
      setStatus('error');

      const mapped = mapAuthError(error);
      if (mapped.ourFault) {
        authError(`signin-infra:${mapped.code}`, error, { method: 'apple_signin', error_type: mapped.type, in_app_browser: isInAppBrowser() });
      } else {
        authBreadcrumb(`signin-user-error:${mapped.code}`, { method: 'apple_signin', in_app_browser: isInAppBrowser() });
      }

      const errorMessage = mapped.message;
      const errorType = mapped.type;

      setError(errorMessage);

      // Track auth error
      logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_ERROR, {
        method: 'apple_signin',
        error_type: errorType,
        error_message: errorMessage,
        time_taken: Date.now() - authStartTime,
        email_provided: false,
        timestamp: Date.now()
      });
    }
  };

  // Hosted-OAuth buttons (v1 — Google redirect + Apple redirect).
  const renderGoogleV1 = (widthClass: string) => (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={status === 'loading'}
      data-testid="google-v1-button"
      className={`${widthClass} flex justify-center items-center py-3 px-4 border border-neutral-300 rounded-lg bg-neutral-50 text-base font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
    >
      {status === 'loading' ? (
        <LoadingSpinner />
      ) : (
        <>
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Google</span>
        </>
      )}
    </button>
  );

  const renderAppleV1 = (widthClass: string) => (
    <button
      type="button"
      onClick={handleAppleSignIn}
      disabled={status === 'loading'}
      data-testid="apple-v1-button"
      className={`${widthClass} flex justify-center items-center py-3 px-4 border border-neutral-300 rounded-lg bg-neutral-50 text-base font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
    >
      {status === 'loading' ? (
        <LoadingSpinner />
      ) : (
        <>
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          <span>Apple</span>
        </>
      )}
    </button>
  );

  return (
    <div className="min-h-screen flex bg-neutral-50">
      <Helmet>
        <title>Sign In | Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="Sign in to your Social Work Test Prep Academy account to access adaptive ASWB exam preparation with personalized analytics."
        />
        <meta property="og:title" content="Sign In | Social Work Test Prep Academy" />
        <meta property="og:description" content="Access your personalized ASWB exam preparation dashboard with adaptive learning technology." />
      </Helmet>

      {/* LEFT — Pam's warm welcome panel. Hidden below md so the form stays
          primary on phones (this is a logged-out route: no real streak/number).
          Rendered as an <aside> with a non-heading display line so the page has
          exactly one "Welcome back" heading (the form's h1). */}
      <aside className="hidden md:flex md:w-2/5 lg:w-[42%] flex-col justify-center gap-6 bg-primary-500 p-14">
        <img
          src={publicAsset('pamela-image.png')}
          alt="Pam, your SWTPA clinical supervisor"
          className="w-24 h-24 rounded-full object-cover border-4 border-primary-700"
        />
        <p className="font-display font-bold text-white text-4xl leading-tight max-w-sm">
          Welcome back to your corner.
        </p>
        <p className="font-sans font-semibold text-primary-100 text-base leading-relaxed max-w-sm">
          I kept your spot. Pick up right where we left off. Your plan&apos;s still warm.
        </p>
        <p className="font-sans font-bold text-primary-100 text-sm flex items-center gap-2 pt-1">
          <span aria-hidden="true">🔥</span> Your streak is waiting
        </p>
      </aside>

      {/* RIGHT — the sign-in form on warm cream */}
      <main className="flex-1 flex flex-col justify-center items-center p-6 sm:p-10">
        <div className="w-full max-w-sm flex flex-col gap-5 animate-fade-in-up opacity-0">
          {/* Logo mark */}
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-white"
                aria-hidden="true"
              >
                <path d="M12 7v14" />
                <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
              </svg>
            </div>
            <span className="font-display font-bold text-neutral-900 text-xl">SWTPA</span>
          </div>

          {/* Heading */}
          <h1 className="font-display font-bold text-neutral-900 text-3xl">
            Welcome back.
          </h1>

          {/* Sign In Form */}
          <form onSubmit={handleAuth} className="flex flex-col gap-5" data-testid="signin-form">
            {status === 'error' && (
              <div className="bg-error-light rounded-2xl p-4 border-l-4 border-error animate-fade-in" role="alert" data-testid="error-message">
                <p className="text-sm text-neutral-700">{error}</p>
              </div>
            )}
            {/* Hidden username field for password managers */}
            <input
              type="text"
              name="username"
              value={email}
              onChange={() => {}} // Read-only, synced with email
              autoComplete="username"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
              readOnly
            />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="eyebrow text-neutral-600">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 254))} // Security: Limit email length
                required
                maxLength={254}
                pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                autoComplete="username"
                data-testid="email-input"
                aria-label="Email"
                className="w-full px-4 py-3.5 rounded-2xl bg-white border-2 border-neutral-200 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-sans"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="eyebrow text-neutral-600">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs font-bold text-primary-500 hover:text-primary-600 transition-colors">
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="password-input"
                aria-label="Password"
                className="w-full px-4 py-3.5 rounded-2xl bg-white border-2 border-neutral-200 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-sans"
              />
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              data-testid="submit-login"
              className="w-full flex items-center justify-center py-3.5 rounded-2xl bg-primary-500 text-white font-display font-bold text-base shadow-ledge-brand hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? <LoadingSpinner /> : 'Sign in'}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-xs font-bold text-neutral-500">or</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>

            <AgeConfirm
              checked={ageConfirmed}
              onChange={(checked) => {
                setAgeConfirmed(checked);
                if (checked && error === AGE_REQUIRED_MESSAGE) {
                  setStatus('idle');
                  setError(null);
                }
              }}
            />

            {/* OAuth Buttons — hosted redirect (v1). */}
            <div className="flex flex-col sm:flex-row gap-3">
              {renderGoogleV1('flex-1')}
              {renderAppleV1('flex-1')}
            </div>
          </form>

          <p className="text-center text-sm text-neutral-600">
            New here?{' '}
            <Link
              to="/try"
              className="inline-flex min-h-[44px] items-center font-bold text-primary-500 hover:text-primary-600 transition-colors"
            >
              Get 7 free days
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SignIn;